import { SafeDynamicIcon } from '@Pages/pageRuntime/components/ui/SafeDynamicIcon';
import { cn } from '@Pages/pageRuntime/lib/utils';
import type React from 'react';

type StatCardProps = {
  /**
   * Metric label.
   */
  title: React.ReactNode;

  /**
   * Primary metric value.
   */
  value: React.ReactNode;

  /**
   * Optional supporting context.
   */
  description?: React.ReactNode;

  /**
   * Lucide icon name shown in the top-right of the card.
   */
  icon?: string;

  /**
   * Trend or comparison text.
   */
  trend?: React.ReactNode;

  className?: string;
};

/**
 * A compact metric card for dashboard summaries and KPIs. Use in small groups near the
 * top of dashboards or overview pages.
 *
 * @example
 * <Sdk.Components.StatCard title="Active students" value={studentCount} icon="Users" trend="Up 12%" />
 */
export function StatCard({ title, value, description, icon, trend, className }: StatCardProps) {
  return (
    <div
      className={cn(
        'rounded-xl border border-border bg-card p-5 text-card-foreground shadow-sm',
        className
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-sm font-medium text-muted-foreground">{title}</p>
          <div className="mt-2 text-3xl font-semibold tracking-tight">{value}</div>
        </div>
        {icon ? (
          <div className="rounded-lg bg-muted p-2 text-muted-foreground">
            <SafeDynamicIcon name={icon} className="size-5" />
          </div>
        ) : null}
      </div>
      {description || trend ? (
        <div className="mt-4 flex flex-col gap-2 text-sm text-muted-foreground">
          {description ? <p>{description}</p> : null}
          {trend ? <div>{trend}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
